import React, { useContext, useMemo } from "react";
import moment from "moment";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import RpImageTextInline from "@/components/RpImageTextInline";
import { USER_ROLE } from "@/config/constants";
import TagChipContainer from "@/components/muiCustom/chip/TagChip";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import { getCampaignStatusText, getStatusCode } from "@/helpers/utils";
import { DIRECT_MAIL_CAMP_PROVIDER } from "../utils/directMailConstants";

const Detail = ({ title = "", children, image = "", svgIcon = "" }) => {
  return (
    <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
      <RpImageTextInline
        image={image}
        svgIcon={svgIcon}
        imageProps={{ color: "primary" }}
        text={
          <>
            <div className="title">{title}</div>
            {children}
          </>
        }
        alignCenter={false}
      />
    </div>
  );
};

export default function DirectMailCampaignReportDetail({ campaign, values }) {
  const { loggedInUserRole, loggedInUser } = useContext(GlobalContext);

  const getScheduleDates = (campaign) => {
    if (campaign?.provider === DIRECT_MAIL_CAMP_PROVIDER.MAIL_SHARK) {
      const date = campaign?.schedule_date;
      const start = moment(date);
      const end = start.isValid() ? moment(start).add(4, "day") : null;
      if (date && start.isValid()) {
        return `${start.format("M/D/YYYY")} - ${end.format("M/D/YYYY")}`;
      }
      return "N/A";
    } else if (campaign?.provider === DIRECT_MAIL_CAMP_PROVIDER.RRD) {
      return campaign?.mailing_date_week_range
        ? campaign?.mailing_date_week_range
        : "N/A";
    } else {
      return "N/A";
    }
  };

  const groupName = useMemo(() => {
    const { group_id } = values;
    const { group_name, group_list } = campaign || {};

    const group = group_list?.find(
      (g) => String(g.groups_id) === String(group_id)
    );
    if (group_name) {
      return group_name?.[0]?.group_name;
    } else if (group) {
      return group.group_name;
    }
    return "All";
  }, [campaign, values]);

  const locationsCount =
    campaign?.campaignDetails?.[campaign.campaignDetails.length - 1]
      ?.location_count ?? 0;
  const participantCount =
    campaign?.campaignDetails?.[campaign.campaignDetails.length - 1]
      ?.participant_count ?? 0;

  const totalBudget = campaign?.campaignDetails
    ? campaign.campaignDetails.reduce((total, campaignDetail) => {
        const budget =
          campaignDetail?.total_budget ?? campaignDetail?.total_cost ?? 0;
        return total + Number(budget);
      }, 0)
    : 0;

  const campaignStatus =
    (campaign?.getCampaignProvider === DIRECT_MAIL_CAMP_PROVIDER.RRD &&
      campaign?.report_status === "Delivered") ||
    campaign?.report_status === 1 ||
    campaign?.status === 1
      ? 6
      : 2;

  const renderTags = () => {
    return (
      <>
        <div className="col-12 p-0 ml-3">
          <RpImageTextInline
            svgIcon="tags"
            imageProps={{ color: "primary" }}
            text={<div className="title">Tags</div>}
            alignCenter={false}
          />
          {campaign?.tags && campaign?.tags?.length > 0 && (
            <div className="col mb-3 ml-1">
              <TagChipContainer tags={campaign?.tags || []} />
            </div>
          )}
        </div>
      </>
    );
  };

  const renderDetails = () => (
    <div className="row h-100">
      {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
        <Detail title="Name" svgIcon="person">
          <div className="value main">
            {`${loggedInUser.first_name} ${loggedInUser.last_name}`}
          </div>
        </Detail>
      )}
      <Detail title="Campaign Name" svgIcon="campaign">
        <div className="value main">{campaign?.campaign_name || "N/A"}</div>
      </Detail>

      <Detail title="Scheduled Date" svgIcon="campaign_calender">
        <div className="value">{getScheduleDates(campaign)}</div>
      </Detail>

      <Detail title="Total Cost" svgIcon="unique_id">
        <div className="value main">
          <CurrencyFormateText value={totalBudget.toFixed(2)} suffix=" USD" />
        </div>
      </Detail>

      <Detail title="Locations" svgIcon="location_mark">
        <div className="value">{locationsCount}</div>
      </Detail>

      {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
        <Detail title="Participants" svgIcon="users">
          <div className="value">{participantCount}</div>
        </Detail>
      )}

      <Detail title="Unique ID" svgIcon="unique_id">
        <div className="value us-all">
          {campaign?.system_generated_campaign_id || "N/A"}
        </div>
      </Detail>

      {campaign?.provider === DIRECT_MAIL_CAMP_PROVIDER.RRD ? (
        <Detail title="Status" svgIcon="campaign">
          <div className="value us-all">
            <StatusChip
              label={getCampaignStatusText(campaignStatus)}
              status={getStatusCode(campaignStatus)}
              showOnlyLabel
            />
          </div>
        </Detail>
      ) : null}

      {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
        <Detail title="Group(s)" svgIcon="home">
          <div className="value main">{groupName}</div>
        </Detail>
      )}
      {campaign?.tags && campaign?.tags?.length > 0 && renderTags()}
    </div>
  );

  return (
    <div className="row align-items-stretch postcard-campaign-report">
      <div className="col">{renderDetails()}</div>
    </div>
  );
}
